import { type ChangeEvent, useState } from 'react';
import { InputGroup, Spinner } from '@blueprintjs/core';
import { useDebouncedValue } from '@gonfalon/async/useDebouncedValue';
import { buildOffsetPaginationProps } from '@gonfalon/pagination/buildOffsetPaginationProps';
import { Pagination } from '@gonfalon/pagination/component';

import {
  Card,
  CopyToClipboard,
  DefaultCallout,
  EnvironmentColorIndicator,
  ErrorCallout,
  Module,
  ModuleContent,
  ModuleHeader,
  type RequestError,
} from '../../shared';
import { Box, Flex } from '../../shared/components';
import {
  MIN_SEARCH_TERM_LENGTH,
  PROJECT_MATCH_LIMIT,
  PROJECTS_PAGE_SIZE,
  type ProjectSearchResults,
  type SearchFailures,
  useProjectEnvironments,
  useProjectsByKeys,
  useProjectSearch,
  useProjectsPage,
} from '../hooks/useProjects';
import type { Account, Environment, Project } from '../model';

import { useClientPagination } from './RolesAndTeams/useClientPagination';

const SEARCH_DEBOUNCE_MS = 300;
const EMPTY_RESULTS: ProjectSearchResults = { projects: [], truncated: false };

type AccountProps = Readonly<{ account: Account }>;

/**
 * The "Projects & environments" tab: a search box over a paged list of project
 * modules, each showing its environment cards. Browsing pages through the
 * account's projects on the server; searching matches projects by name, key or ID
 * and environments by anything the account-wide environment search indexes.
 */
export function ProjectsPanel({ account }: AccountProps) {
  const [searchInput, setSearchInput] = useState('');
  const term = useDebouncedValue(searchInput.trim(), SEARCH_DEBOUNCE_MS);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => setSearchInput(event.target.value);

  return (
    <div>
      <Flex mb={2}>
        <Box width={1 / 4}>
          <InputGroup
            leftIcon="search"
            placeholder="Search projects and environments..."
            value={searchInput}
            onChange={handleSearchChange}
          />
        </Box>
      </Flex>
      <ProjectResults account={account} term={term} />
    </div>
  );
}

function ProjectResults({ account, term }: AccountProps & { term: string }) {
  if (term.length >= MIN_SEARCH_TERM_LENGTH) {
    return <ProjectSearchResults account={account} term={term} />;
  }
  return <ProjectsBrowser account={account} />;
}

function ProjectsBrowser({ account }: AccountProps) {
  const [offset, setOffset] = useState(0);
  const { data, isPending, isPlaceholderData, error } = useProjectsPage(account, offset);

  if (error) {
    return <ProjectsError error={error} />;
  }
  if (isPending || !data) {
    return <Spinner size={25} />;
  }

  // Paginate from the offset the shown page was fetched at, not the requested
  // one: while the next page loads the list still shows the previous page.
  return (
    <>
      <ProjectModules account={account} projects={data.items} />
      <ProjectsPagination
        {...buildOffsetPaginationProps(data.totalCount, PROJECTS_PAGE_SIZE, data.offset, setOffset)}
        loading={isPlaceholderData}
      />
    </>
  );
}

function ProjectSearchResults({ account, term }: AccountProps & { term: string }) {
  const { data, isPending, error } = useProjectSearch(account, term);
  const results = data ?? EMPTY_RESULTS;
  const page = useClientPagination({ items: results.projects, pageSize: PROJECTS_PAGE_SIZE, resetKey: term });
  const projects = useProjectsWithIds(account, page.pageItems);

  if (error) {
    return <ProjectsError error={error} />;
  }
  if (isPending) {
    return <Spinner size={25} />;
  }
  if (results.projects.length === 0) {
    return (
      <>
        <SearchHalfFailedNotice failures={results.failures} />
        <DefaultCallout>No projects or environments match your search.</DefaultCallout>
      </>
    );
  }

  return (
    <>
      <SearchHalfFailedNotice failures={results.failures} />
      <TruncatedMatchesNotice truncated={results.truncated} />
      <ProjectModules account={account} projects={projects} />
      <ProjectsPagination
        from={page.from}
        to={page.to}
        total={page.totalCount}
        onFirstClick={page.hasPreviousPage ? page.gotoFirstPage : undefined}
        onPreviousClick={page.hasPreviousPage ? page.gotoPreviousPage : undefined}
        onNextClick={page.hasNextPage ? page.gotoNextPage : undefined}
        onLastClick={page.hasNextPage ? page.gotoLastPage : undefined}
      />
    </>
  );
}

// Projects reached only through an environment match arrive without an ID; look
// the IDs up for the ones on screen so their module header matches the others.
function useProjectsWithIds(account: Account, projects: Project[]): Project[] {
  const missing = projects.filter((project) => !project._id).map((project) => project.key);
  const { data } = useProjectsByKeys(account, missing);
  const idByKey = new Map((data?.items ?? []).map((project) => [project.key, project._id]));
  return projects.map((project) => (project._id ? project : { ...project, _id: idByKey.get(project.key) ?? '' }));
}

function TruncatedMatchesNotice({ truncated }: { truncated: boolean }) {
  if (!truncated) {
    return null;
  }
  return (
    <Box p={2}>
      <DefaultCallout>
        Only the first {PROJECT_MATCH_LIMIT} projects matching by name or key are listed. Narrow the search to see the
        rest.
      </DefaultCallout>
    </Box>
  );
}

function SearchHalfFailedNotice({ failures }: { failures?: SearchFailures }) {
  if (failures?.environments) {
    return (
      <Box p={2}>
        <ErrorCallout heading="Environment search failed; showing project matches only">
          {requestErrorText(failures.environments)}
        </ErrorCallout>
      </Box>
    );
  }
  if (failures?.projects) {
    return (
      <Box p={2}>
        <ErrorCallout heading="Project search failed; showing environment matches only">
          {requestErrorText(failures.projects)}
        </ErrorCallout>
      </Box>
    );
  }
  return null;
}

const requestErrorText = (error: RequestError) =>
  typeof error.message === 'string' ? error.message : JSON.stringify(error.message);

function ProjectsError({ error }: { error: RequestError }) {
  return <ErrorCallout heading="Unable to load projects">{requestErrorText(error)}</ErrorCallout>;
}

type PaginationProps = ReturnType<typeof buildOffsetPaginationProps> & { loading?: boolean };

function ProjectsPagination({ loading = false, ...props }: PaginationProps) {
  if (props.total <= PROJECTS_PAGE_SIZE) {
    return null;
  }
  return (
    <Flex justifyContent="flex-end" alignItems="center" p={2}>
      <PageLoadingIndicator loading={loading} />
      <Pagination {...props} />
    </Flex>
  );
}

function PageLoadingIndicator({ loading }: { loading: boolean }) {
  if (!loading) {
    return null;
  }
  return (
    <Box pr={2}>
      <Spinner size={16} />
    </Box>
  );
}

function ProjectModules({ account, projects }: AccountProps & { projects: Project[] }) {
  return (
    <>
      {projects.map((project) => (
        <ProjectModule key={project.key} account={account} project={project} />
      ))}
    </>
  );
}

function ProjectModule({ account, project }: AccountProps & { project: Project }) {
  return (
    <Flex>
      <Box width={1} p={2}>
        <Module>
          <ModuleHeader>
            <span>
              {project.name}{' '}
              <CopyToClipboard content={project.key}>
                <code>{project.key}</code>
              </CopyToClipboard>{' '}
              <ProjectIdChip id={project._id} />
            </span>
          </ModuleHeader>
          <ModuleContent snug seamless>
            <ProjectEnvironments account={account} project={project} />
          </ModuleContent>
        </Module>
      </Box>
    </Flex>
  );
}

function ProjectIdChip({ id }: { id: string }) {
  if (!id) {
    return null;
  }
  return (
    <CopyToClipboard content={id}>
      <code>{id}</code>
    </CopyToClipboard>
  );
}

// The projects list inlines at most 20 environments per project; fetch the full
// list for the projects that have more, showing the inlined ones meanwhile.
function ProjectEnvironments({ account, project }: AccountProps & { project: Project }) {
  const truncated = project.environmentsTotalCount > project.environments.length;
  const { data, error } = useProjectEnvironments(account, project.key, truncated);
  // The query is keyed by project only, so another consumer (browse, the audit
  // log swatch) may have cached data or an error for it; ignore both unless
  // this module needs the full list.
  const fullList = truncated ? data : undefined;
  const environments = fullList ? fullList.items : project.environments;

  return (
    <Flex flexWrap="wrap">
      {environments.map((environment) => (
        <EnvironmentCard key={environment._id} environment={environment} />
      ))}
      <RemainingEnvironmentsStatus
        pending={truncated && !fullList}
        error={truncated ? error : null}
        remaining={project.environmentsTotalCount - environments.length}
        notShown={fullList ? fullList.totalCount - fullList.items.length : 0}
      />
    </Flex>
  );
}

function RemainingEnvironmentsStatus({
  pending,
  error,
  remaining,
  notShown,
}: {
  pending: boolean;
  error: RequestError | null;
  remaining: number;
  notShown: number;
}) {
  if (error) {
    return (
      <Box width={1} p={2}>
        <ErrorCallout heading={`Unable to load the remaining ${remaining} environments`}>
          {requestErrorText(error)}
        </ErrorCallout>
      </Box>
    );
  }
  if (pending) {
    return (
      <Box width={1} p={2} className="pt-text-muted">
        Loading {remaining} more environments…
      </Box>
    );
  }
  // The environments endpoint returns at most 1000 per page.
  if (notShown > 0) {
    return (
      <Box width={1} p={2} className="pt-text-muted">
        {notShown} more environments not shown.
      </Box>
    );
  }
  return null;
}

function EnvironmentCard({ environment }: { environment: Environment }) {
  return (
    <Box width={1 / 3} p={2}>
      <Card>
        <Flex flexDirection="column">
          <Box pb={2}>
            <EnvironmentColorIndicator color={environment.color} />
            {environment.name}{' '}
            <CopyToClipboard content={environment.key}>
              <code>{environment.key}</code>
            </CopyToClipboard>
          </Box>
          <SecretRow label="SDK key" value={environment.apiKey} />
          <SecretRow label="Mobile key" value={environment.mobileKey} />
          <Box pb={2}>
            <div className="pt-text-muted">Environment ID</div>{' '}
            <CopyToClipboard content={environment._id}>
              <code>{environment._id}</code>
            </CopyToClipboard>
          </Box>
        </Flex>
      </Card>
    </Box>
  );
}

function SecretRow({ label, value }: { label: string; value?: string }) {
  if (!value) {
    return null;
  }
  return (
    <Box pb={2}>
      <div className="pt-text-muted">{label}</div>{' '}
      <CopyToClipboard content={value}>
        <code>{value}</code>
      </CopyToClipboard>
    </Box>
  );
}
